<template>
  <DeferredContent @on-load-component="handleFetchReportSummary">
    <div class="rounded-md flex flex-col gap-3">
      <div
        class="w-full flex flex-col justify-center items-center sm:h-32 h-24 gap-3"
      >
        <ElAvatar :size="50" fit="scale-down" :src="profilePhoto">
          <UserIcon class="text-untitled-gray-600 w-6" />
        </ElAvatar>
        <p class="font-semibold text-untitled-gray-600 text-sm">  
          {{ fullName }}
        </p>
        <div class="flex flex-col items-center gap-2">
            <span
              v-if="roles"
              class="text-xs font-medium text-untitled-gray-600 capitalize"
            >
              ({{ roles }})
            </span>
          <div
            v-if="isLoadingAuth"
            class="h-6 w-36 rounded-full bg-untitled-gray-200 animate-pulse"
          />
          <ElSwitch v-else-if="isTeamLeader"
            class="switch-as-role"
            v-model="isActingAsTeamLeader"
            @change="switchAsTeamLeader"
            active-text="Set as Team leader"
          />
        </div>
      </div>  
      <div class="w-full">
        <ul
          role="list"
          class="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-2 w-full"
          v-if="!loadingSummary"
        >
          <SummaryCard
            v-for="report in reportSummaryCards"
            :key="report.key"
            :text="report.title"
            :value="report.value"
            :prop="report.key"
            @on-click-card="handleClickCard"
          />
        </ul>
        <LoadingSummaryCards v-else />
      </div>
    </div>
    <CompletedCoursesModal
      v-if="showCompletedCoursesModal"
      v-model="showCompletedCoursesModal"
    />
    <CourseInProgressModal
      v-if="showInProgressCoursesModal"
      v-model="showInProgressCoursesModal"
    />
    <AssignedCourseModal
      v-if="showAssignedCoursesModal"
      v-model="showAssignedCoursesModal"
    />
    <CertificatesEarnedModal
      v-if="showCertificatesEarnedModal"
      v-model="showCertificatesEarnedModal"
    />
  </DeferredContent>
</template>

<script setup>
import { useAuthStore } from "@/stores";
import { UserIcon } from "@heroicons/vue/outline";
import { ElAvatar, ElSwitch } from "element-plus";
import { computed, defineAsyncComponent, nextTick, onMounted, onUnmounted } from "vue";
import SummaryCard from "./SummaryCard.vue";
import { useReportSummaryStore } from "@/stores/home/useReportSummary.store";
import { storeToRefs } from "pinia";
import LoadingSummaryCards from "./partials/LoadingSummaryCards.vue";
import DeferredContent from "@/components/utilities/DeferredContent.vue";
import { ref,watch, inject } from "vue";
import { useDialogStore } from "@/stores/useDialogStore";
import { getRoles } from "@/composable/users/getRoles.js";
import { useTeamProgressStore } from "@/stores/home/useTeamProgress.store";


const CertificatesEarnedModal = defineAsyncComponent(() =>
  import("@/components/home/my-progress/CertificatesEarnedModal.vue")
);
const AssignedCourseModal = defineAsyncComponent(() =>
  import("@/components/home/my-progress/AssignedCourseModal.vue")
);
const CourseInProgressModal = defineAsyncComponent(() =>
  import("@/components/home/my-progress/CourseInProgressModal.vue")
);
const CompletedCoursesModal = defineAsyncComponent(() =>
  import("@/components/home/my-progress/CompletedCoursesModal.vue")
);


const axios = inject("axios");
const dialogStore = useDialogStore();
const authStore = useAuthStore();
const reportSummaryStore = useReportSummaryStore();
const teamProgressStore = useTeamProgressStore();
const { loadingSummary } = storeToRefs(reportSummaryStore);

const isTeamLeader = computed(() => {
  const isLeader = getRoles().isTeamLeaderWithoutActingAsTeamLeader.value

  return isLeader && (getRoles()?.isSuperAdmin?.value || getRoles().isAccountManager?.value || getRoles().isSpecificManager?.value || getRoles().isMultiStoreManager?.value || getRoles().isRegionManager?.value)
});
const roles = computed(() => authStore.rolesFriendly?.[0] ?? "");
const profilePhoto = computed(() => authStore.user?.profile_picture);
const fullName = computed(() => {
  if(authStore.user){
    return authStore.user.name
  }
  return "Guest User"
});
const reportSummaryCards = computed(
  () => reportSummaryStore.reportSummaryCards
);
const showCompletedCoursesModal = ref(false);
const showInProgressCoursesModal = ref(false);
const showAssignedCoursesModal = ref(false);
const showCertificatesEarnedModal = ref(false);
const isLoadingAuth = ref(true);

const handleFetchReportSummary = () => {
  reportSummaryStore.handleFetchReportSummary();
};

const switchAsTeamLeader = () => {
  teamProgressStore.loadingProgress = true;
  axios.put("/api/v3/switch-role").then(() => {
    teamProgressStore.handleFetchAssignedSummary();
    window.location.reload();
  }).catch((error) => {
    console.log(error);
  });
}

const isActingAsTeamLeader = ref(authStore?.user?.is_team_leader);

onMounted(async () => {
  isLoadingAuth.value = false;
  await nextTick();

  try {
    await authStore.getAuth();
    isActingAsTeamLeader.value = authStore?.user?.is_team_leader;
  } catch (error) {
    console.log(error);
  }
});


const handleClickCard = (prop) => {
  if (prop === "courses_completed_count") {
    showCompletedCoursesModal.value = !showCompletedCoursesModal.value;
    return;
  }
  if (prop === "courses_in_progress_count") {
    showInProgressCoursesModal.value = !showInProgressCoursesModal.value;
    return;
  }
  if (prop === "total_assigned_count") {
    showAssignedCoursesModal.value = !showAssignedCoursesModal.value;
    return;
  }

  showCertificatesEarnedModal.value = !showCertificatesEarnedModal.value;
};

const hideHeader = (value) => {
  if(value){
    dialogStore.zIndexValue = 0;
  }
  else{
    dialogStore.zIndexValue = 40;
  }

  dialogStore.isDialogVisible = value
}

watch(showAssignedCoursesModal,(value) => {
  hideHeader(value)
})

watch(showCertificatesEarnedModal,(value) => {
  hideHeader(value)
})

watch(showCompletedCoursesModal,(value) => {
  hideHeader(value)
})

watch(showInProgressCoursesModal,(value) => {
  hideHeader(value)
})
onUnmounted(() => {
  reportSummaryStore.handleAbortFetchReportSummary()
})
</script>

<style scoped></style>